// types
import {
	Account,
	Comment,
	Conversion,
	Document,
	Loan,
	Payment,
	Transaction,
	TransactionWithDaEquivalent,
	User,
} from '../@types';

// session
const sessionUser = sessionStorage.getItem('user');
const user = sessionUser ? JSON.parse(sessionUser) : {};

// vars
const msCurrent = new Date().getTime();
const msDay = 86400000;
const msNewTimeframe = msDay * 30;

export const zeroAddress = '0x0000000000000000000000000000000000000000';

export interface EnhanceCommentsParams {
	comments: Comment[];
}

export const enhanceComments = ({ comments }: EnhanceCommentsParams) => {
	for (let i = 0; i < comments.length; i++) {
		const comment = comments[i];
		const commentUserId =
			typeof comment.user === 'number' ? comment.user : (comment.user as User)?.id;

		// define default vars
		comment.isNew = false;

		// if comment was created by a different user than logged in user
		if ((!user.id && commentUserId) || (user.id && user.id !== commentUserId)) {
			const msCreated = new Date(comment.created).getTime();
			const msDiff = msCurrent - msCreated;

			// redefine isNew based on the "new" timeframe
			comment.isNew = msDiff < msNewTimeframe;
		}
	}

	return comments;
};

export interface EnhanceDocumentsParams {
	documents: Document[];
}

export const enhanceDocuments = ({ documents }: EnhanceDocumentsParams) => {
	for (let i = 0; i < documents.length; i++) {
		const document = documents[i];
		const documentUserId =
			typeof document.user === 'number' ? document.user : (document.user as User)?.id;

		// define default vars
		document.isNew = false;

		// if document was created by a different user than logged in user
		if ((!user.id && documentUserId) || (user.id && user.id !== documentUserId)) {
			const msCreated = new Date(document.created).getTime();
			const msDiff = msCurrent - msCreated;

			// redefine isNew based on the "new" timeframe
			document.isNew = msDiff < msNewTimeframe;
		}
	}

	return documents;
};

export interface AddDaEquivalentPropsToTransactionsParams {
	loanId: number;
	transactions: Transaction[];
}

export const addDaEquivalentPropsToTransactions = ({
	loanId,
	transactions,
}: AddDaEquivalentPropsToTransactionsParams): TransactionWithDaEquivalent[] => {
	return transactions.map((t: Transaction) => {
		let amountLoan: null | number = null;
		let amountLoanDaEquivalent: null | number = null;
		let currencyDa: null | string = null;
		let exchangeRateDa: null | number = null;
		let exchangeRateBaseCurrency: null | string = null;
		let exchangeRateQuoteCurrency: null | string = null;
		let taxLoan: null | number = null;
		let taxLoanDaEquivalent: null | number = null;

		if (t.payments.length && loanId) {
			// find payment for the specific loan
			const payment = t.payments.find((p) => (p.loan as Loan).id === Number(loanId));

			if (payment) {
				// Always set these for display in the "Amount" and "Tax" columns
				amountLoan = payment.amount;
				taxLoan = payment.tax || 0;

				// Determine the DA currency from designatedAccount or fromAccount
				const daCurrency =
					(t.designatedAccount as Account)?.currency || (t.fromAccount as Account)?.currency;

				// Check if this is a DA account transaction
				const isDaAccountTransaction =
					t.fromAccount && typeof t.fromAccount === 'object' && t.fromAccount.type === 'DESIGNATED';

				// Check if there are conversions
				if (payment.conversions && payment.conversions.length > 0) {
					// Find the DA equivalent conversion
					const conversion = payment.conversions.find((c) => daCurrency === c.currency);

					if (conversion) {
						// Found conversion to DA currency
						amountLoanDaEquivalent = conversion.amount;
						taxLoanDaEquivalent = conversion.tax || 0;
						exchangeRateDa = conversion.exchangeRate;
						currencyDa = conversion.currency;
						exchangeRateBaseCurrency = payment.currency;
						exchangeRateQuoteCurrency = conversion.currency;
					} else if (isDaAccountTransaction && payment.currency === daCurrency) {
						// Payment is from DA account in DA currency, but conversions are to other currencies
						// No actual conversion needed - set exchange rate fields to null
						amountLoanDaEquivalent = payment.amount;
						taxLoanDaEquivalent = payment.tax || 0;
						currencyDa = daCurrency;
						exchangeRateDa = null;
						exchangeRateBaseCurrency = null;
						exchangeRateQuoteCurrency = null;
					}
					// If conversion exists but not to DA currency and not a DA account payment, leave DA equivalent values as null (N/A)
				} else if (isDaAccountTransaction || t.type === 'ADVANCE' || t.type === 'REIMBURSEMENT') {
					// No conversion exists but this is a DA transaction
					const currency = daCurrency || payment.currency || t.currency;

					// Set DA equivalent values (same as payment amount since no conversion needed)
					amountLoanDaEquivalent = payment.amount;
					taxLoanDaEquivalent = payment.tax || 0;
					currencyDa = currency || null;
					// Exchange rate fields are null when transaction is in DA currency with no conversions
					exchangeRateDa = null;
					exchangeRateBaseCurrency = null;
					exchangeRateQuoteCurrency = null;
				}
				// If no conversion and not a DA account transaction, leave DA equivalent values as null (N/A)
			}
		}

		return {
			...t,
			amountLoan,
			amountLoanDaEquivalent,
			currencyDa,
			exchangeRateDa,
			exchangeRateBaseCurrency,
			exchangeRateQuoteCurrency,
			taxLoan,
			taxLoanDaEquivalent,
		};
	});
};

interface CalcTransactionTotalByCurrencyWithPaymentsParams {
	currency?: string;
	payments: Payment[];
	transaction?: Transaction;
}

interface CalcTransactionTotalByCurrencyWithTransactionParams {
	currency?: string;
	payments?: Payment[];
	transaction: Transaction;
}

export type CalcTransactionTotalByCurrencyParams =
	| CalcTransactionTotalByCurrencyWithPaymentsParams
	| CalcTransactionTotalByCurrencyWithTransactionParams;

export const calcTransactionTotalByCurrency = ({
	currency,
	payments,
	transaction,
}: CalcTransactionTotalByCurrencyParams) => {
	const targetCurrency = currency || transaction?.currency || payments?.[0]?.currency;
	let paymentsToSum: Payment[] = [];

	if (payments) {
		paymentsToSum = payments;
	} else if (transaction && Array.isArray(transaction.payments)) {
		paymentsToSum = transaction.payments.filter((p) => typeof p === 'object');
	}

	let hasMatch = false;
	const total = paymentsToSum.reduce((sum, p: Payment) => {
		if (p.currency === targetCurrency) {
			hasMatch = true;
			return sum + Number(p.amount || 0) + Number(p.tax || 0);
		} else if (p.conversions?.length) {
			const conversion = p.conversions.find((c) => c.currency === targetCurrency);
			if (conversion) {
				hasMatch = true;
				return sum + Number(conversion.amount || 0) + Number(conversion.tax || 0);
			}
		}
		return sum;
	}, 0);

	return hasMatch ? total : null;
};

export type TransactionDisplayField = 'amount' | 'applied' | 'reimbursed' | 'tax' | 'total';

export interface GetTransactionDisplayValueParams {
	currency?: string;
	field: TransactionDisplayField;
	loan?: number;
	transaction: Transaction;
}

export interface TransactionDisplayValue {
	displayAmount: number | undefined;
	displayConversions: Conversion[];
	displayCurrency: string | undefined;
}

const getPaymentFieldValue = (
	payment: Payment,
	field: TransactionDisplayField
): number | undefined => {
	if (field === 'amount') return payment.amount;
	if (field === 'tax') return payment.tax || undefined;
	if (field === 'applied' || field === 'reimbursed') return payment.reimbursed?.amount || undefined;
	return payment.tax != null ? payment.amount + payment.tax : payment.amount;
};

const getConversionFieldValue = (
	conversion: Conversion,
	field: TransactionDisplayField
): number => {
	if (field === 'amount') return conversion.amount;
	if (field === 'tax') return conversion.tax;
	return conversion.amount + (conversion.tax || 0);
};

export const getTransactionDisplayValue = ({
	currency,
	field,
	loan,
	transaction,
}: GetTransactionDisplayValueParams): TransactionDisplayValue => {
	let displayAmount: number | undefined;
	let displayCurrency = transaction.currency;
	let displayConversions: Conversion[] = transaction.conversions || [];
	let paymentForLoan: Payment | undefined;

	// if loan is specified, find its payment
	if (loan) {
		paymentForLoan = transaction.payments?.find((p) => p.loan === loan);
	}

	// if currency is specified, override currency
	if (currency) {
		displayCurrency = currency;
	}

	if (field === 'applied') {
		displayAmount = 0;
		displayConversions = [];

		const appliedTransactions = (transaction.transactions || []) as Transaction[];

		appliedTransactions.forEach((appliedTransaction) => {
			const payments = (appliedTransaction.payments || []) as Payment[];

			payments.forEach((payment) => {
				if (!payment.reimbursed?.amount) return;

				if (!currency || payment.currency === currency) {
					// native currency matches — use reimbursed amount directly
					if (typeof displayAmount === 'number') {
						displayAmount += payment.reimbursed.amount;
					}
				} else {
					// find conversion for the specified currency
					const conversionForCurrency = payment.reimbursed.conversions?.find(
						(c) => c.currency === currency
					);

					if (conversionForCurrency && typeof displayAmount === 'number') {
						displayAmount += conversionForCurrency.amount;
					}

					// add native payment currency as a synthetic conversion entry
					const existingConversion = displayConversions.find(
						(c) => c.currency === payment.currency
					);
					if (existingConversion) {
						existingConversion.amount += payment.reimbursed.amount;
					} else {
						displayConversions.push({
							amount: payment.reimbursed.amount,
							currency: payment.currency,
							exchangeRate: null,
							id: null,
							tax: null,
						} as unknown as Conversion);
					}
				}
			});
		});

		// filter the primary currency out of conversions
		if (currency) {
			displayConversions = displayConversions.filter((c) => c.currency !== currency);
		}
	} else {
		if (field === 'reimbursed') {
			displayAmount = 0;
			displayConversions = [];

			if (transaction.payments) {
				if (loan) {
					if (paymentForLoan?.reimbursed?.amount) {
						displayAmount = paymentForLoan.reimbursed.amount;
						displayConversions = paymentForLoan.reimbursed.conversions || [];
					}
				} else {
					transaction.payments.forEach((payment) => {
						if (payment.reimbursed?.amount && typeof displayAmount === 'number') {
							displayAmount += payment.reimbursed.amount;

							// sum reimbursement conversions with same currency
							payment.reimbursed.conversions?.forEach((conversion) => {
								const existingConversion = displayConversions.find(
									(c) => c.currency === conversion.currency
								);
								if (existingConversion) {
									existingConversion.amount += conversion.amount;
								} else {
									displayConversions.push({ ...conversion });
								}
							});
						}
					});
				}
			}

			if (currency && transaction.currency !== currency) {
				const conversionForCurrency = displayConversions.find((c) => c.currency === currency);

				// inject native reimbursed amount as a synthetic conversion entry
				displayConversions = [
					{
						amount: displayAmount,
						currency: transaction.currency,
						exchangeRate: null,
						id: null,
						tax: null,
					} as unknown as Conversion,
					...displayConversions,
				];

				displayAmount = conversionForCurrency?.amount || 0;
			}
		} else {
			if (currency) {
				if (loan) {
					if (paymentForLoan) {
						if (transaction.currency === currency) {
							// native currency matches — use payment field value directly
							displayAmount = getPaymentFieldValue(paymentForLoan, field);
							displayConversions = paymentForLoan.conversions || [];
						} else {
							// find the conversion for the specified currency in the loan payment
							const conversionForCurrency = paymentForLoan.conversions?.find(
								(c) => c.currency === currency
							);

							if (conversionForCurrency) {
								displayAmount = getConversionFieldValue(conversionForCurrency, field);
							}

							// add native payment currency as a synthetic conversion entry
							displayConversions = [
								{
									amount: paymentForLoan.amount,
									currency: paymentForLoan.currency,
									exchangeRate: null,
									id: null,
									tax: paymentForLoan.tax || null,
								} as unknown as Conversion,
								...(paymentForLoan.conversions || []),
							];
						}
					}
				} else {
					if (transaction.currency === currency) {
						// native currency matches — use transaction field value directly
						// fall back to summing payments when the field is absent at the top level (e.g. REIMBURSEMENT)
						displayAmount = transaction[field];
						if (displayAmount == null && transaction.payments?.length) {
							const sum = transaction.payments.reduce((acc, p) => {
								if (p.currency === currency) {
									return acc + (getPaymentFieldValue(p, field) || 0);
								}
								const conv = p.conversions?.find((c) => c.currency === currency);
								return conv ? acc + getConversionFieldValue(conv, field) : acc;
							}, 0);
							displayAmount = sum || undefined;
						}
					} else if (transaction.currency == null && transaction.payments?.length) {
						// no top-level currency (e.g. REIMBURSEMENT) — sum across payments
						const sum = transaction.payments.reduce((acc, p) => {
							if (p.currency === currency) {
								return acc + (getPaymentFieldValue(p, field) || 0);
							}
							const conv = p.conversions?.find((c) => c.currency === currency);
							return conv ? acc + getConversionFieldValue(conv, field) : acc;
						}, 0);
						displayAmount = sum || undefined;
					} else {
						// find the conversion for the specified currency
						const conversionForCurrency = displayConversions.find((c) => c.currency === currency);
						if (conversionForCurrency) {
							displayAmount = getConversionFieldValue(conversionForCurrency, field);
						}

						// add native transaction currency as a synthetic conversion entry
						// only when the transaction has a known currency and amount
						if (transaction.currency != null && transaction.amount != null) {
							displayConversions = [
								{
									amount: transaction.amount,
									currency: transaction.currency,
									exchangeRate: null,
									id: null,
									tax: transaction.tax || null,
								} as unknown as Conversion,
								...displayConversions,
							];
						}
					}
				}
			} else {
				if (loan) {
					if (paymentForLoan) {
						displayAmount = getPaymentFieldValue(paymentForLoan, field);
						displayConversions = paymentForLoan.conversions || [];
					}
				} else {
					displayAmount = transaction[field];
				}
			}
		}

		// filter the primary currency out of conversions
		if (currency) {
			displayConversions = displayConversions.filter((c) => c.currency !== currency);
		}
	}

	return {
		displayAmount,
		displayConversions,
		displayCurrency,
	};
};
